import React from "react";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import type { ThirdDataRequest } from "../../models/ThirdDataRequest";
import { ThirdDataRequestCreateDialog } from "./third-data-request-create";

const getUserPhone = (record: ThirdDataRequest): string => {
  const phone = record.of?.phone ?? record.of?.mobile ?? record.of?.phoneNumber ?? "";
  return phone || "-";
};

const ListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const [client, setClient] = React.useState<string>(String(filterValues?.client ?? ""));
  const [createOpen, setCreateOpen] = React.useState(false);
  const refresh = useRefresh();

  React.useEffect(() => {
    setClient(String(filterValues?.client ?? ""));
  }, [filterValues?.client]);

  const applyFilter = () => {
    const trimmed = client.trim();
    const nextFilters: Record<string, unknown> = { ...(filterValues ?? {}) };
    if (trimmed) {
      nextFilters.client = trimmed;
    } else {
      delete nextFilters.client;
    }
    setFilters(nextFilters, undefined, false);
  };

  const resetFilter = () => {
    setClient("");
    const nextFilters: Record<string, unknown> = { ...(filterValues ?? {}) };
    delete nextFilters.client;
    setFilters(nextFilters, undefined, false);
  };

  return (
    <>
      <TopToolbar
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          px: 0,
          py: 1,
          minHeight: "auto",
        }}
      >
        <MuiButton
          variant="contained"
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(25, 118, 210, 0.24)",
          }}
        >
          创建预授权
        </MuiButton>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ ml: "auto", flexWrap: "wrap" }}
        >
          <MuiTextField
            size="small"
            value={client}
            label="客户端ID"
            placeholder="请输入客户端ID"
            onChange={(e) => setClient(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilter();
              }
            }}
            sx={{
              width: 280,
              minWidth: 220,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
              },
            }}
          />
          <MuiButton
            variant="outlined"
            onClick={resetFilter}
            sx={{ borderRadius: 2, px: 2, whiteSpace: "nowrap" }}
          >
            重置
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={applyFilter}
            sx={{ borderRadius: 2, px: 2, whiteSpace: "nowrap" }}
          >
            查询
          </MuiButton>
        </Stack>
      </TopToolbar>
      <ThirdDataRequestCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => refresh()}
      />
    </>
  );
};

const ThirdDataRequestListContent: React.FC = () => {
  const { total, page, perPage, setPage } = useListContext<ThirdDataRequest>();
  const totalPages = Math.max(1, Math.ceil((total ?? 0) / perPage));

  return (
    <Box sx={{ mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(148, 163, 184, 0.16)",
          background:
            "linear-gradient(180deg, rgba(15, 23, 42, 0.02) 0%, rgba(15, 23, 42, 0.04) 100%)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              预授权记录列表
            </Typography>
            <Typography variant="body2" color="text.secondary">
              展示客户端、用户与身份信息授权记录
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <MuiButton
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              上一页
            </MuiButton>
            <Typography variant="body2" color="text.secondary">
              第 {page} / {totalPages} 页
            </Typography>
            <MuiButton
              size="small"
              variant="outlined"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              下一页
            </MuiButton>
            <Typography
              variant="body2"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                color: "primary.main",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              共 {total ?? 0} 条
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={{
              minWidth: 1100,
              "& .RaDatagrid-headerCell": {
                fontWeight: 700,
                backgroundColor: "rgba(15, 23, 42, 0.04)",
              },
              "& .RaDatagrid-row:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
              "& td.sticky-actions": {
                backgroundColor: "background.paper",
              },
              "& th.sticky-actions": {
                zIndex: 4,
                backgroundColor: "rgba(15, 23, 42, 0.04)",
              },
              "& .sticky-actions": {
                position: "sticky",
                right: 0,
                zIndex: 3,
                borderLeft: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "-12px 0 18px rgba(2, 6, 23, 0.06)",
              },
            }}
          >
            <TextField source="id" label="ID" />
            <TextField source="client.name" label="客户端" />
            <TextField source="client._id" label="客户端ID" />
            <TextField source="of.name" label="用户" />
            <TextField source="type" label="类型" />
            <DateField source="createdAt" label="创建时间" showTime />
            <FunctionField
              label="操作"
              headerClassName="sticky-actions"
              cellClassName="sticky-actions"
              render={() => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <RequestDetailButton />
                  <DeleteRequestButton />
                </Stack>
              )}
            />
          </Datagrid>
        </Box>
      </Paper>
    </Box>
  );
};

const RequestDetailDialog: React.FC<{
  open: boolean;
  requestId: string;
  onClose: () => void;
}> = ({ open, requestId, onClose }) => {
  const dataProvider = useDataProvider();
  const [record, setRecord] = React.useState<ThirdDataRequest | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    dataProvider
      .getOne("thirdDataRequests", { id: requestId })
      .then((res: { data: ThirdDataRequest }) => {
        if (cancelled) return;
        setRecord(res.data);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, requestId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>预授权详情</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2">加载中...</Typography>
        ) : record ? (
          <Stack spacing={1}>
            <Typography variant="body2">预授权ID：{record.id}</Typography>
            <Typography variant="body2">
              客户端：{record.client?.name}（{record.client?._id}）
            </Typography>
            <Typography variant="body2">
              用户：{record.of?.name}（{record.of?.phone}）
            </Typography>
            <Typography variant="body2">类型：{record.type}</Typography>
            <Box
              sx={{
                mt: 1,
                borderRadius: 2,
                border: "1px solid rgba(148, 163, 184, 0.24)",
                backgroundColor: "rgba(15, 23, 42, 0.02)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid rgba(148, 163, 184, 0.18)" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  data
                </Typography>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  px: 1.5,
                  py: 1,
                  fontSize: 12,
                  lineHeight: 1.6,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(record.data ?? null, null, 2);
                  } catch {
                    return String(record.data ?? "");
                  }
                })()}
              </Box>
            </Box>
            <Typography variant="body2">创建时间：{record.createdAt}</Typography>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>关闭</MuiButton>
      </DialogActions>
    </Dialog>
  );
};

const RequestDetailButton: React.FC = () => {
  const record = useRecordContext<ThirdDataRequest>();
  const [open, setOpen] = React.useState(false);

  if (!record) return null;

  return (
    <>
      <MuiButton size="small" onClick={() => setOpen(true)}>
        详情
      </MuiButton>
      <RequestDetailDialog
        open={open}
        requestId={record.id}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const DeleteRequestButton: React.FC = () => {
  const record = useRecordContext<ThirdDataRequest>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  if (!record) return null;

  const onDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await dataProvider.delete("thirdDataRequests", {
        id: record.id,
        previousData: record,
      });
      notify("删除成功", { type: "success" });
      refresh();
      setConfirmOpen(false);
    } catch {
      notify("删除失败", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <MuiButton size="small" color="error" onClick={() => setConfirmOpen(true)}>
        删除
      </MuiButton>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">即将要删除的授权信息：</Typography>
            <Typography variant="body2" color="text.primary">
              客户端：{record.client?.name ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.primary">
            用户：{record.of?.name ?? "-"}（{getUserPhone(record)}）
            </Typography>
            <br />
            <Typography variant="body2">确认删除该预授权记录？</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton disabled={deleting} onClick={() => setConfirmOpen(false)}>
            取消
          </MuiButton>
          <MuiButton disabled={deleting} color="error" onClick={onDelete}>
            {deleting ? "删除中..." : "确认删除"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ThirdDataRequestList: React.FC = (props: Record<string, unknown>) => (
  <List
    {...props}
    empty={false}
    actions={<ListActions />}
    sx={{
      "& .RaList-main": {
        mt: 1,
      },
      "& .RaList-content": {
        backgroundColor: "transparent",
        boxShadow: "none",
      },
    }}
  >
    <ThirdDataRequestListContent />
  </List>
);
